import { Component, effect } from "@angular/core";
import { MainModel } from "../../../global/model/MainModel";
import { CampaignSettingsService } from "../../services/campaign-settings.service";
import { ConfirmationService, MessageService } from "primeng/api";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { GridDisplayColumn } from "../../../global/model/SearchModel";
import { CardModule } from "primeng/card";
import { GenericButtonComponent } from "../../../global/component/generic/generic-button/generic-button.component";
import { GenericTableComponent } from "../../../global/component/generic/generic-table/generic-table.component";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DialogModule } from "primeng/dialog";
import { NgIf } from "@angular/common";
import { CheckbookLU, FundCodeLU, FundType } from "../../models/CommonModels";
import { LookupService } from "../../services/lookup.service";
import { DropdownModule } from "primeng/dropdown";
import { ToggleButtonModule } from "primeng/togglebutton";
import { ToastModule } from "primeng/toast";
import { SharedService } from "../../services/shared.service";
import { CampaignService } from "../../services/campaign.service";
import { ChildCampaignApiResponse } from "../../models/UserInviteModel";
import { MultiSelectModule } from "primeng/multiselect";

@Component({
    selector: 'app-campaign-settings-fundcodes',
    standalone: true,
    imports: [
        CardModule, GenericButtonComponent, GenericTableComponent,
        ConfirmDialogModule, DialogModule,
        FormsModule, ReactiveFormsModule, ToastModule,
        NgIf, DropdownModule, ToggleButtonModule, MultiSelectModule
    ],
    providers: [CampaignSettingsService, ConfirmationService, LookupService,
                MessageService, SharedService, CampaignService],
    templateUrl: './campaign-settings-accouting-systems.component.html',
    styleUrl: './campaign-settings-accouting-systems.component.scss'
})
export class CampaignSettingsAccountingSystemsComponent {
  campaignId: number = -1;
  isEntityCampaign: boolean = false;
  showAddEditDialog: boolean = false;
  isEditMode: boolean = false;  
  addEditFundCode: FormGroup;
  fundTypes?: FundType[] | undefined;
  checkbooks?: CheckbookLU[] | undefined;
  fundcodes?: any[] | undefined;
  campaignFundcodes: Record<number, FundCodeLU[]> = {};

  childCampaigns?: ChildCampaignApiResponse[] | undefined;

  data: any[] = [];
  readonly CAMPAIGN_COLUMN_INDEX: number = 0;
  columns: GridDisplayColumn[] = [
      {
        columnName: "campaign",
        displayColumn: "Campaign",
        displayColumnType: "text"
      },
      {
        columnName: "fundCode",
        displayColumn: "Fundcode",
        displayColumnType: "text",
        isSortable: true,
        isFilterable: true
      },
      {
        columnName: "checkbooks",
        displayColumn: "Checkbooks",
        displayColumnType: "formattedText"
      },
      {
        columnName: "edit",
        displayColumn: "Actions",
        displayColumnType: "actions"
      }
    ];

      
  constructor(private mainModel: MainModel,
              private campaignSettingsService: CampaignSettingsService,
              private confirmationService: ConfirmationService,
              private lookupService: LookupService,
              private messageService: MessageService,
              private sharedService: SharedService,
              private campaignService: CampaignService,
              private fb: FormBuilder) {

    this.addEditFundCode = this.fb.group({
      fundCodeId: [0],      
      childCampaignId: ['', this.isEntityCampaign ? Validators.required : ''],
      selectedCheckbooks: [[], Validators.required]
    });

    effect(() => {

      const c: any = this.mainModel.selectedCampaign();
      const campaignId: number = parseInt(c?.campaignId);
      // If campaign is not loaded, then we can't load donor flags
      if (isNaN(campaignId))
        return;

      this.campaignId = campaignId;
      this.isEntityCampaign = c.isEntity;
      this.loadFundcodeCheckbooks();
      this.loadChildCampaigns();
      // this.loadFundCodes();
    });

  }

  loadChildCampaigns(): void {

    if (this.isEntityCampaign)
    {
      this.campaignService.getUserChildCampaigns(this.campaignId).subscribe({
        next: data => {
          this.childCampaigns = data;
          const tempColumns = [...this.columns];
          const campaignColumn = tempColumns[this.CAMPAIGN_COLUMN_INDEX];
          campaignColumn.isSortable = true;
          campaignColumn.isFilterable = true;
          campaignColumn.filterType = "MultiSelect";
          campaignColumn.filterOptions = this.childCampaigns.map(cc => { 
            return {
              key: cc.name, 
              value: cc.name
            }; 
          });
          this.columns = tempColumns; 
        },
        error: err => {
          console.error('Unable to load child campaigns. Please try again later.', err);
        }
      });
    }
  }

  loadFundcodeCheckbooks(): void {

    this.campaignSettingsService.getFundcodeCheckbooks(this.campaignId).subscribe({
      next: data => {
        this.data = data;
      },
      error: err => {
        console.error('Unable to load fundcode checkbooks. Please try again later.', err);
      },
      complete: () => console.debug("Funcode checkbooks data loading completed.")
    });
  }  

  formattedText(columnName: string, fundCodeCheckbooks: any) {
    switch(columnName)
    {
      case "checkbooks":
        let checkbooksFormattedText = '';
        for (const cb of fundCodeCheckbooks?.checkbooks) {
          checkbooksFormattedText += `<b>${cb.checkbookId}</b> (<i>${cb.description}</i>) <br />`
        }
        return checkbooksFormattedText;
    }
    return '';
  }

  loadFundTypes() {
    // Load if not loaded already
    if (!this.fundTypes)
    {
      this.lookupService.getFundTypes().subscribe((data: any) => {
        this.fundTypes = data;
      });
    }
  }

  loadCheckbooks() {
    // Load if not loaded already
    if (!this.checkbooks)
    {
      this.lookupService.getCampaignCheckbooks(this.campaignId).subscribe((data: any) => {
        this.checkbooks = data;
      });
    }
  }

  loadFundCodes(childCampaignId?: number | undefined) {

    if (Object.keys(this.campaignFundcodes).length <= 0)
    {
      this.campaignSettingsService.getFundcodes(this.campaignId).subscribe({
        next: data => {

          if (this.isEntityCampaign) {
            const childCampaignIds = data.map(fc => fc.childCampaignId);
            const uniqueChildCampaignIds = [... new Set(childCampaignIds)];
            for (const cc of uniqueChildCampaignIds) {
              const childCampaignFundcodes = data.filter(fc => fc.childCampaignId == cc)
              this.campaignFundcodes[this.campaignId] = childCampaignFundcodes.map(fc => {
                return {
                  key: fc.fundCodeId,
                  value: fc.fundCode
                };
              });
            }

          }
          else {
            this.campaignFundcodes[this.campaignId] = data.map(fc => {
              return {
                key: fc.fundCodeId,
                value: fc.fundCode
              };
            });
          }
          
          if (!childCampaignId)
            this.fundcodes = this.campaignFundcodes[this.campaignId];
          else
            this.fundcodes = this.campaignFundcodes[childCampaignId];

        },
        error: err => {
          console.error('Unable to load fundcodes. Please try again later.', err);
        },
        complete: () => console.debug("Funcodes data loading completed.")
      });
    }
    else {
        if (!childCampaignId)
          this.fundcodes = this.campaignFundcodes[this.campaignId];
        else
          this.fundcodes = this.campaignFundcodes[childCampaignId];
    }
    
  }

  onActionClicked(eventData: any) {
    switch(eventData.type) {
      case "edit":
        this.onAddEditFundCodeCheckbooks(eventData.data);
        break;
      // case "delete":
      //   this.onDeleteFundCode(eventData.data);
      //   break;
    }
  }  

  onCloseAddEditDialog() {
    this.showAddEditDialog = false;
  }

  onAddEditFundCodeCheckbooks(fundCode?: any) {
    this.loadCheckbooks();
    this.loadFundCodes();
    if (!fundCode) {
      this.isEditMode = false;
      // new fund code
      fundCode = {
        fundCodeId: 0,
        childCampaignId: '',
        selectedCheckbooks: []
      };
    } else {
      this.isEditMode = true;
    }
    this.addEditFundCode.reset();
    this.addEditFundCode.patchValue(fundCode);

    const selectedCheckbooks = fundCode.checkbooks?.map((cb: { dexRowId: number }) => cb.dexRowId.toString());
    this.addEditFundCode.patchValue({selectedCheckbooks: selectedCheckbooks});
    
    this.showAddEditDialog = true;
  }

  onSaveFundcodeCheckbooks() {
    if (this.isEntityCampaign && this.addEditFundCode.value.childCampaignId === "") {
      this.addEditFundCode.get('childCampaignId')?.markAllAsTouched();
    }
    if(!this.addEditFundCode.value.fundCodeId) {
      this.addEditFundCode.get('fundCodeId')?.markAsTouched();
    }
    if(this.addEditFundCode.value.selectedCheckbooks?.count <= 0) {
      this.addEditFundCode.get('funselectedCheckbooksdTypeId')?.markAsTouched();
    }

    if (this.addEditFundCode.valid) {

      const fundCodeCheckbooks: any = {
        fundCodeId: parseInt(this.addEditFundCode.value.fundCodeId || '', 10),
        campaignId: this.campaignId,
        childCampaignId: this.addEditFundCode.value.childCampaignId || '',
        selectedCheckbooks: this.addEditFundCode.value.selectedCheckbooks || [],
        isDeleted: false
      };

      this.campaignSettingsService.saveFundcodeCheckbooks(fundCodeCheckbooks).subscribe({
        next: (res) => { 
          window.location.reload();
        },
        error: (result) => {
          var errorMessage = 'An error occurred while trying to save fundcode checkbooks.';
          if (result?.error?.message)
            errorMessage = this.sharedService.decodeHtml(result.error.message);
          this.messageService.add({ severity: 'error', summary: 'Error', detail: errorMessage });
        }
      });
    }

  }
}